import {PropsWithChildren, useMemo} from "react";
import {createContext} from "../../../../../context/create-context";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {Button, Result} from "antd";
import {useParams} from "react-router-dom";
import {GetProductApiResponse} from "../../../../../react-query-hooks/products/types";
import {CreateSessionApiBookingQuestionsPayload} from "../../../../../react-query-hooks/session/types";
import {useDigitalProduct} from "../../../../../features/digital-products";
import {DigitalProduct} from "../../../../../features/digital-products/domain/digital-product.entity";
import {BookingQuestion} from "../../../../../features/digital-products/domain/digital-product.types";

type GetProductState = {
  productData: GetProductApiResponse;
  isFetchingProduct: boolean;
  isLoadingProduct: boolean;
};

type GetProductContextState = {
  state: GetProductState;
};

const [useContext, ContextProvider] = createContext<GetProductContextState>();

export const useGetProductContext = useContext;

/**
 * Maps domain booking question to legacy API format.
 * Just renames answerType to answer_type (snake_case).
 */
function mapBookingQuestionToLegacy(
  q: BookingQuestion
): CreateSessionApiBookingQuestionsPayload {
  const base = {question: q.question, required: q.required, answer_type: q.answerType};

  if (
    q.answerType === "checkbox" ||
    q.answerType === "dropdown" ||
    q.answerType === "radio-button"
  ) {
    return {...base, options: q.options ?? []};
  }
  return base as CreateSessionApiBookingQuestionsPayload;
}

/**
 * Adapter: maps DigitalProduct entity to legacy GetProductApiResponse shape.
 * This allows existing components to continue working during the v2 migration.
 */
function adaptDigitalProductToLegacy(product: DigitalProduct): GetProductApiResponse {
  const rawCurrencies = product.currencies;
  const bookingQuestions = product.bookingQuestions;

  // Filter out currencies with null/undefined currency code (can happen with free products from v2)
  // For free products, the form expects an empty array
  const currencies = product.isFree()
    ? []
    : rawCurrencies.filter((c) => c.currency != null);

  return {
    product: {
      id: product.id,
      userId: product.userId,
      title: product.name,
      slug: product.slug,
      description: product.description,
      coverImage: product.coverImage ?? "",
      cover_images: [],
      hideFromProfile: !product.isVisibleOnProfile(),
      hideVariantFromProfile: product.hideVariantFromProfile,
      request_phone_number: product.requestPhoneNumber,
      purchaseLimit: product.purchaseLimit,
      is_deleted: false,
      statusId: String(product.statusId),
      reference: product.reference,
      url_redirect: product.urlRedirect,
      redirectType: product.redirectType,
      telegramGroupChatId: product.telegramGroupChatId,
      tiny_url: product.tinyUrl,
      createdAt: product.createdAt,
      updatedAt: product.updatedAt,
      enable_currency_conversion: product.enableCurrencyConversion,
      is_pay_what_you_want: product.isPayWhatYouWant,
      selected_currencies: product.selectedCurrencies,
      bookingQuestions: bookingQuestions.map(mapBookingQuestionToLegacy),
    },
    totalSales: 0,
    medias: [],
    revenue: [],
    currencies: currencies.map((c, index) => ({
      id: index,
      userId: product.userId,
      service_id: product.id,
      service_type: 1,
      currency: c.currency,
      amount: c.amount,
      min_call_price: c.minCallPrice,
      is_auto_converted: c.isAutoConverted ?? false,
      is_base:
        c.currency.toUpperCase() === product.callPriceCurrency.toUpperCase() ||
        c.isBase === true,
      createdAt: product.createdAt,
      updatedAt: product.updatedAt,
    })),
    // Add-ons/variants are not carried on the product entity under v2 — they
    // have a dedicated endpoint and are fetched separately by the variants tab
    // via `useDigitalProductAddOns`. This empty array is intentional.
    variants: {variantsWithOptions: []},
  };
}

export const GetProductProvider = ({children}: PropsWithChildren) => {
  const {id} = useParams();
  const getProductQuery = useDigitalProduct(Number(id));

  const productData = useMemo(() => {
    if (!getProductQuery.data) return null;
    return adaptDigitalProductToLegacy(getProductQuery.data);
  }, [getProductQuery.data]);

  if (getProductQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (getProductQuery.isError) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching product"
        extra={
          <Button type="primary" size="small" onClick={() => getProductQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  if (!productData) {
    return <FullPageLoader />;
  }

  const state: GetProductState = {
    productData,
    isFetchingProduct: getProductQuery.isFetching,
    isLoadingProduct: getProductQuery.isLoading,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
